import { createUUID } from '@insurgrid/common';

const KEY = 'insurgridTraceId';
const TTL_MS = 60 * 60 * 1000; // 1hr idle → new session

type Stored = { traceId: string; lastSeen: number };

/**
 * Resolve the session traceId. Call once on page load (client-side). The
 * layout owns this (passes the `?traceId=` override); other consumers use
 * `readTraceId()`.
 *
 * Precedence: explicit param → stored-within-TTL → mint. Every call refreshes
 * `lastSeen`, so an active multi-step session never expires mid-flow — only
 * >1hr between loads (or an explicit param) starts a new session.
 */
export function resolveTraceId(paramTraceId?: string | null): string {
  const now = Date.now();
  if (paramTraceId) return persist(paramTraceId, now); // explicit → new/owned session
  const prev = read();
  if (prev && now - prev.lastSeen < TTL_MS) return persist(prev.traceId, now);
  return persist(createUUID(), now);
}

/**
 * Read-only accessor for consumers (analytics, client.create). Reuse-or-mint;
 * never overrides an active session.
 */
export function readTraceId(): string {
  return resolveTraceId();
}

function read(): Stored | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? 'null');
  } catch {
    return null; // corrupt value → treat as absent, remint
  }
}

function persist(traceId: string, lastSeen: number): string {
  try {
    localStorage.setItem(KEY, JSON.stringify({ traceId, lastSeen }));
  } catch {
    // No storage (SSR) or it's disabled/full — the id still serves this
    // render; the client remints and persists on hydration.
  }
  return traceId;
}
